import { useEffect, useState } from 'react'
import { dayName as sayDay, type StaffLanguage } from './staffText'

/**
 * One clock for every timer on the screen.
 *
 * Every wait on this product is drawn from `Date.now()` at render time, which
 * means it only moves when something else causes a render. On a busy branch a
 * message arrives every few seconds and nobody notices; on a quiet one — the last
 * hour of the day, a single customer sitting in the hall — a wait of "4m 12s" sits
 * unchanged for two minutes and then jumps to "6m 30s". The number looks live and
 * is not, which is worse than a number that admits it is a snapshot, because an
 * operator deciding whether somebody has been waiting too long is reading a figure
 * that stopped moving.
 *
 * One interval for the whole document rather than one per component: a counter
 * screen with twelve rows in the line would otherwise hold thirteen timers that
 * fire at thirteen slightly different moments, so the column of durations ticks
 * raggedly down the page.
 *
 * It stops while the tab is hidden. A hall board left running overnight has no
 * reader, and a phone with the branch app in the background should not be woken
 * once a second to recompute a duration nobody is looking at. On return the clock
 * is read immediately, so the first frame after a tab is focused is current rather
 * than up to a second stale.
 */

type Subscriber = (at: number) => void

const subscribers = new Set<Subscriber>()
let timer: ReturnType<typeof setInterval> | undefined

function fire() {
  const at = Date.now()
  for (const notify of subscribers) notify(at)
}

function running(): boolean {
  return typeof document === 'undefined' || document.visibilityState === 'visible'
}

function start() {
  if (timer !== undefined || subscribers.size === 0 || !running()) return
  timer = setInterval(fire, 1000)
}

function stop() {
  if (timer === undefined) return
  clearInterval(timer)
  timer = undefined
}

function onVisibility() {
  if (running()) {
    fire()
    start()
  } else {
    stop()
  }
}

/** The current time in milliseconds, re-read once a second while the tab is visible. */
export function useNow(): number {
  const [at, setAt] = useState(() => Date.now())

  useEffect(() => {
    subscribers.add(setAt)
    if (subscribers.size === 1 && typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisibility)
    }
    start()
    // Read once on mount rather than waiting up to a second for the first tick,
    // so a timer that appears mid-second is not a second behind the one beside it.
    setAt(Date.now())

    return () => {
      subscribers.delete(setAt)
      if (subscribers.size === 0) {
        stop()
        if (typeof document !== 'undefined') {
          document.removeEventListener('visibilitychange', onVisibility)
        }
      }
    }
  }, [])

  return at
}

/** Whole seconds between an ISO instant and now, never negative.
 *
 *  Never negative because a browser clock a few seconds ahead of the server would
 *  otherwise render "-3s" beside a customer who has just been called, and a
 *  negative duration reads as a bug in the product rather than in the clock. */
export function secondsSince(iso: string | undefined, now: number): number {
  if (!iso) return 0
  const at = Date.parse(iso)
  if (Number.isNaN(at)) return 0
  return Math.max(0, Math.floor((now - at) / 1000))
}

/**
 * A business date — "2026-09-15" — as a person would say it.
 *
 * Built from the parts rather than handed to `new Date(text)`, because that parses
 * a bare date as UTC midnight and then formats it in the reader's zone: west of
 * Greenwich every business date would name the day before. The value has no time
 * and no zone in it, and constructing it locally is what keeps it that way.
 *
 * Falls back to the original text if it is not a date, so a malformed value shows
 * as itself rather than as "Invalid Date" on a supervisor's screen.
 */
export function dayName(businessDate: string, lang: StaffLanguage): string {
  return sayDay(businessDate, lang)
}
